import { useCallback, useEffect, useMemo, useState } from 'react';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
// Cross-feature: 라이더 목록 API — partner/supplierFee 기능과 공유. 추후 shared/api로 통합 검토 필요.
import { supplierFeeApi } from '@/features/partner/supplierFee/riderToPartner/api/supplierFeeApi';
import { RiderDto } from '@/features/partner/supplierFee/riderToPartner/types/supplierFee.types';
import { promoMissionApi } from '../api/promoMissionApi';
import { promoSuppSettlementApi, PromoSuppSettlementDto } from '../api/promoSuppSettlementApi';
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { PartnerPortalLoginResponse } from '@/features/auth/types/auth.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { formatDateToISO } from '@/shared/utils/dateUtils';
import {
    Mission, MissionType, PeriodType, MissionTypeCode, PeriodCode,
    FALLBACK_MISSION_TYPE_CODES, FALLBACK_PERIOD_CODES,
} from '../types/promotion.types';
import { toMission } from '../utils/promotionHelpers';

export interface UsePromotionDataReturn {
    isReadOnly: boolean;
    suppId: string | null;
    today: string;
    missions: Mission[];
    missionsLoading: boolean;
    fetchMissions: () => Promise<void>;
    riders: RiderDto[];
    ridersLoading: boolean;
    ridersLoaded: boolean;
    fetchRiders: () => Promise<void>;
    missionTypeCodes: MissionTypeCode[];
    periodCodes: PeriodCode[];
    settlementStartDate: string;
    setSettlementStartDate: (v: string) => void;
    settlementEndDate: string;
    setSettlementEndDate: (v: string) => void;
    settlements: PromoSuppSettlementDto[];
    settlementsLoading: boolean;
    fetchSettlements: () => Promise<void>;
    handleForceProcess: (s: PromoSuppSettlementDto) => Promise<void>;
    handleSwOnly: (s: PromoSuppSettlementDto) => Promise<void>;
}

export function usePromotionData(): UsePromotionDataReturn {
    const { showToast } = useToast();

    // 페이지 진입 시 1회만 읽음 — 렌더마다 새 참조가 생기는 훅 함수 대신 getItem 직접 사용
    const isReadOnly = useMemo(() => getItem<string>('USER_TYPE') !== 'SUPPLIER', []);
    // 백엔드 로그인 응답에 suppId가 없음 — 협력사 계정은 userId === suppId
    const suppId = useMemo(() => {
        const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
        return userInfo?.suppId ?? userInfo?.userId ?? null;
    }, []);
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const [missions, setMissions] = useState<Mission[]>([]);
    const [missionsLoading, setMissionsLoading] = useState(false);
    const [riders, setRiders] = useState<RiderDto[]>([]);
    const [ridersLoading, setRidersLoading] = useState(false);
    const [ridersLoaded, setRidersLoaded] = useState(false);
    const [missionTypeCodes, setMissionTypeCodes] = useState<MissionTypeCode[]>(FALLBACK_MISSION_TYPE_CODES);
    const [periodCodes, setPeriodCodes] = useState<PeriodCode[]>(FALLBACK_PERIOD_CODES);
    const [settlementStartDate, setSettlementStartDate] = useState('');
    const [settlementEndDate, setSettlementEndDate] = useState('');
    const [settlements, setSettlements] = useState<PromoSuppSettlementDto[]>([]);
    const [settlementsLoading, setSettlementsLoading] = useState(false);

    // TODO: 백엔드에 MISSION_TYPE, MISSION_PERIOD 공통코드 그룹 등록 후 정상 응답
    const fetchCommonCodes = useCallback(async () => {
        try {
            const res = await commonCodeApi.getCommonCodeList('MISSION_TYPE');
            const valid = res.codes.filter((c) => c.useYn === 'Y');
            if (valid.length > 0) {
                setMissionTypeCodes(
                    valid.map((c) => ({
                        code: c.comCode as MissionType,
                        name: c.comCdName,
                        desc: c.val1 || '',
                        rewardLabel: c.val2 || '',
                        rewardUnit: c.val3 || '',
                    }))
                );
            }
        } catch {
            // 공통코드 미등록 시 fallback 유지
        }
        try {
            const res = await commonCodeApi.getCommonCodeList('MISSION_PERIOD');
            const valid = res.codes.filter((c) => c.useYn === 'Y');
            if (valid.length > 0) {
                setPeriodCodes(valid.map((c) => ({ code: c.comCode as PeriodType, name: c.comCdName })));
            }
        } catch {
            // 공통코드 미등록 시 fallback 유지
        }
    }, []);

    // "특정 라이더만" 선택 시 1회 지연 로딩 (마운트 시 호출하지 않음)
    const fetchRiders = useCallback(async () => {
        if (!suppId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }
        try {
            setRidersLoading(true);
            const res = await supplierFeeApi.getRiderList(suppId);
            setRiders(res.riders || []);
            setRidersLoaded(true);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setRidersLoading(false);
        }
    }, [suppId, showToast]);

    const fetchMissions = useCallback(async () => {
        if (!isReadOnly && !suppId) return;
        try {
            setMissionsLoading(true);
            const res = await promoMissionApi.getMissionList(
                isReadOnly ? undefined : suppId ?? undefined
            );
            setMissions(res.missions.map(toMission));
        } catch {
            showToast('미션 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setMissionsLoading(false);
        }
    }, [suppId, isReadOnly, showToast]);

    const fetchSettlements = useCallback(async () => {
        if (!settlementStartDate || !settlementEndDate) {
            showToast('정산 기간을 입력해 주세요.', { type: ToastType.ERROR });
            return;
        }
        try {
            setSettlementsLoading(true);
            const data = await promoSuppSettlementApi.getList(
                settlementStartDate,
                settlementEndDate
            );
            setSettlements(data.filter((s) => s.holdStatus === 'PENDING_DEPOSIT'));
        } catch {
            showToast('보류 현황을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSettlementsLoading(false);
        }
    }, [settlementStartDate, settlementEndDate, showToast]);

    const handleForceProcess = useCallback(
        async (s: PromoSuppSettlementDto) => {
            if (
                !window.confirm(
                    `[${s.suppId}] 협력사를 강제 지급 처리하시겠습니까?\n잔액이 마이너스가 될 수 있습니다.`
                )
            )
                return;
            try {
                const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
                await promoSuppSettlementApi.forceProcess(
                    s.suppId,
                    formatDateToISO(s.acctStDate),
                    formatDateToISO(s.acctEdDate),
                    userInfo?.userId ?? 'UNKNOWN'
                );
                showToast('강제 지급 처리가 완료되었습니다.', { type: ToastType.SUCCESS });
                await fetchSettlements();
            } catch {
                showToast('강제 지급 처리에 실패했습니다.', { type: ToastType.ERROR });
            }
        },
        [showToast, fetchSettlements]
    );

    const handleSwOnly = useCallback(
        async (s: PromoSuppSettlementDto) => {
            if (
                !window.confirm(
                    `[${s.suppId}] 협력사의 주정산만 진행하시겠습니까?\n프로모션 차감(PD)은 이후 입금 또는 강제 지급 시 처리됩니다.`
                )
            )
                return;
            try {
                const userInfo = getItem<PartnerPortalLoginResponse>(STORAGE_KEYS.USER_INFO);
                await promoSuppSettlementApi.processSwOnly(
                    s.suppId,
                    formatDateToISO(s.acctStDate),
                    formatDateToISO(s.acctEdDate),
                    userInfo?.userId ?? 'UNKNOWN'
                );
                showToast('주정산 진행이 완료되었습니다.', { type: ToastType.SUCCESS });
                await fetchSettlements();
            } catch {
                showToast('주정산 진행에 실패했습니다.', { type: ToastType.ERROR });
            }
        },
        [showToast, fetchSettlements]
    );

    useEffect(() => {
        fetchCommonCodes();
        fetchMissions();
    }, [fetchCommonCodes, fetchMissions]);

    return {
        isReadOnly,
        suppId,
        today,
        missions,
        missionsLoading,
        fetchMissions,
        riders,
        ridersLoading,
        ridersLoaded,
        fetchRiders,
        missionTypeCodes,
        periodCodes,
        settlementStartDate,
        setSettlementStartDate,
        settlementEndDate,
        setSettlementEndDate,
        settlements,
        settlementsLoading,
        fetchSettlements,
        handleForceProcess,
        handleSwOnly,
    };
}
